// src/components/ui/SessionExpiryBanner.tsx
import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { brand, fonts } from '@/lib/tokens';

const WARNING_MINUTES = 10;

export function SessionExpiryBanner() {
  const { tokenExpiresAt, refreshSession } = useAuth();
  const [minutesLeft, setMinutesLeft] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!tokenExpiresAt) return;

    function check() {
      const now = new Date();
      const diff = tokenExpiresAt!.getTime() - now.getTime();
      const mins = Math.floor(diff / 60000);
      setMinutesLeft(mins);
    }

    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, [tokenExpiresAt]);

  // Reset dismissed when a new session starts
  useEffect(() => {
    setDismissed(false);
  }, [tokenExpiresAt]);

  if (
    dismissed ||
    minutesLeft === null ||
    minutesLeft > WARNING_MINUTES ||
    minutesLeft < 0
  ) {
    return null;
  }

  async function handleExtend() {
    setIsRefreshing(true);
    try {
      await refreshSession();
      setDismissed(true);
    } catch {
      // refresh failed — client.ts will redirect to login
    } finally {
      setIsRefreshing(false);
    }
  }

  return (
    <div
      style={{
        background: brand.accentTint,
        borderBottom: `1px solid rgba(104, 187, 203,0.3)`,
        padding: '10px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        gap: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          className="material-symbols-outlined"
          style={{ fontSize: 16, color: brand.accent }}
          aria-hidden
        >
          timer
        </span>
        <span
          style={{
            fontFamily: fonts.sans,
            fontSize: 13,
            color: brand.accent,
          }}
        >
          Your session expires in {minutesLeft} minute{minutesLeft !== 1 ? 's' : ''}.
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={() => setDismissed(true)}
          style={{
            background: 'none',
            border: 'none',
            color: brand.accent,
            fontSize: 12,
            fontFamily: fonts.sans,
            cursor: 'pointer',
            opacity: 0.7,
            padding: '4px 8px',
          }}
        >
          Dismiss
        </button>
        <button
          onClick={handleExtend}
          disabled={isRefreshing}
          style={{
            background: brand.accent,
            border: 'none',
            borderRadius: 6,
            color: brand.canvas,
            fontSize: 12,
            fontFamily: fonts.sans,
            fontWeight: 600,
            cursor: isRefreshing ? 'default' : 'pointer',
            padding: '6px 14px',
            opacity: isRefreshing ? 0.6 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {isRefreshing ? 'Extending...' : 'Extend Session'}
        </button>
      </div>
    </div>
  );
}
